import { TransactionType } from "../enums";
import { isWithinDateRange } from "./date";
import type {
  IRewardTransaction,
  ITransactionFilters,
} from "../models/transaction";

/** Transaction types that increase a member's balance. */
const CREDIT_TYPES = new Set<TransactionType>([
  TransactionType.EARN,
  TransactionType.MANUAL_CREDIT,
  TransactionType.BONUS,
  TransactionType.TIER_ADJUSTMENT,
]);

/** Transaction types that decrease a member's balance. */
const DEBIT_TYPES = new Set<TransactionType>([
  TransactionType.REDEEM,
  TransactionType.MANUAL_DEBIT,
  TransactionType.EXPIRE,
  TransactionType.REFUND_ADJUSTMENT,
]);

/** Whether a transaction increases the balance. */
export function isCredit(tx: Pick<IRewardTransaction, "type" | "points">): boolean {
  if (CREDIT_TYPES.has(tx.type)) return true;
  if (DEBIT_TYPES.has(tx.type)) return false;
  // REVERSE and any future types fall back to the sign of the delta.
  return tx.points >= 0;
}

/** Whether a transaction decreases the balance. */
export function isDebit(tx: Pick<IRewardTransaction, "type" | "points">): boolean {
  return !isCredit(tx);
}

/**
 * Client-side filtering used by activity screens for instant feedback while a
 * server round-trip is in flight, and by tests. Mirrors the server filter
 * semantics in {@link ITransactionFilters}.
 */
export function filterTransactions(
  transactions: IRewardTransaction[],
  filters: ITransactionFilters,
): IRewardTransaction[] {
  return transactions.filter((tx) => {
    if (filters.types && filters.types.length > 0 && !filters.types.includes(tx.type)) {
      return false;
    }
    if (
      filters.purchaseTypes &&
      filters.purchaseTypes.length > 0 &&
      (tx.purchaseType == null || !filters.purchaseTypes.includes(tx.purchaseType))
    ) {
      return false;
    }
    if ((filters.from || filters.to) && !isWithinDateRange(tx.effectiveDate, filters.from, filters.to)) {
      return false;
    }
    if (filters.search) {
      const haystack = `${tx.description} ${tx.purchaseReference ?? ""} ${
        tx.eventReference ?? ""
      }`.toLowerCase();
      if (!haystack.includes(filters.search.toLowerCase())) {
        return false;
      }
    }
    return true;
  });
}

/** Sums the signed point deltas of a list of transactions. */
export function sumPoints(transactions: IRewardTransaction[]): number {
  return transactions.reduce((total, tx) => total + tx.points, 0);
}
